import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import InquiryChat from '../components/InquiryChat';
import BookingDateTimePicker from '../components/BookingDateTimePicker';
import LocationPicker from '../components/bookings/LocationPicker';
import { reverseGeocode } from '../utils/geocode';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ServiceDetail = ({ user, onLogout }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [service, setService] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [provider, setProvider] = useState(null);
  const [feeRates, setFeeRates] = useState({ client_fee_rate: 10, provider_fee_rate: 15 });
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [showInquiryChat, setShowInquiryChat] = useState(false);
  const [bookingMessage, setBookingMessage] = useState('');
  const [bookingDate, setBookingDate] = useState('');
  const [bookingTime, setBookingTime] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [bookingDetails, setBookingDetails] = useState({ duration: 1, totalPrice: 0, selectedHours: 1 });
  const [serviceCoords, setServiceCoords] = useState(null);
  const [serviceAddress, setServiceAddress] = useState('');
  const [savedLocations, setSavedLocations] = useState([]);
  const [selectedSavedId, setSelectedSavedId] = useState('');
  const [mapFocus, setMapFocus] = useState(null);
  const [savedLocHint, setSavedLocHint] = useState('');

  useEffect(() => {
    if (showBookingModal) {
      axios.get(`${API}/user/locations`)
        .then((res) => setSavedLocations(Array.isArray(res.data) ? res.data : []))
        .catch(() => setSavedLocations([]));
    }
  }, [showBookingModal]);

  // Whenever a pin is set/moved (manual tap, drag, or "Use my location"),
  // reverse-geocode it into a readable address if we don't already have one.
  useEffect(() => {
    if (serviceCoords && serviceCoords.lat != null && !serviceAddress) {
      reverseGeocode(serviceCoords.lat, serviceCoords.lng).then((a) => {
        if (a) setServiceAddress(a);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceCoords && serviceCoords.lat, serviceCoords && serviceCoords.lng]);

  const handleSelectSavedLocation = async (locId) => {
    setSelectedSavedId(locId);
    setSavedLocHint('');
    if (!locId) return;
    const loc = savedLocations.find((l) => String(l.id) === String(locId));
    if (!loc) return;
    const pinLat = loc.lat != null ? +loc.lat : (Array.isArray(loc.coordinates) && loc.coordinates.length === 2 ? +loc.coordinates[0] : null);
    const pinLng = loc.lng != null ? +loc.lng : (Array.isArray(loc.coordinates) && loc.coordinates.length === 2 ? +loc.coordinates[1] : null);
    if (pinLat != null && pinLng != null) {
      // Saved pin exists — drop it and recenter.
      setServiceCoords({ lat: pinLat, lng: pinLng });
      setServiceAddress(loc.full_address || [loc.street, loc.barangay, loc.city, loc.province].filter(Boolean).join(', '));
      setSavedLocHint('');
    } else {
      // No saved pin — zoom to barangay/city area so the client can drop the exact pin.
      const query = [loc.barangay, loc.city, loc.province, 'Philippines'].filter(Boolean).join(', ');
      try {
        let res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=1`);
        let data = await res.json();
        if ((!data || !data.length) && (loc.city || loc.province)) {
          const cityQ = [loc.city, loc.province, 'Philippines'].filter(Boolean).join(', ');
          res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(cityQ)}&limit=1`);
          data = await res.json();
        }
        if (data && data.length) {
          setServiceCoords(null);
          setMapFocus({ lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon), zoom: 15 });
          setSavedLocHint('Zoomed to your saved area — please tap the map to drop your exact pin.');
        } else {
          setSavedLocHint('Could not locate that area on the map. Please pin your location manually.');
        }
      } catch (e) {
        setSavedLocHint('Could not locate that area on the map. Please pin your location manually.');
      }
    }
  };

  useEffect(() => {
    fetchServiceDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchServiceDetails = async () => {
    try {
      const [serviceRes, reviewsRes, feeRes] = await Promise.all([
        axios.get(`${API}/services/${id}`),
        axios.get(`${API}/services/${id}/reviews`).catch(() => ({ data: [] })),
        axios.get(`${API}/fee-rates`).catch(() => ({ data: { client_fee_rate: 10, provider_fee_rate: 15 } }))
      ]);

      setService(serviceRes.data);
      setReviews(reviewsRes.data);
      setFeeRates(feeRes.data);

      // Fetch provider info
      const providerRes = await axios.get(`${API}/users/${serviceRes.data.provider_id}`);
      setProvider(providerRes.data);
    } catch (error) {
      console.error('Error fetching service details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBooking = async (e) => {
    e.preventDefault();
    if (!user) {
      navigate('/login');
      return;
    }

    const effectiveRole = user.active_role || user.role;
    if (effectiveRole !== 'client') {
      alert(t('serviceDetail.alertClientsOnly'));
      return;
    }

    if (!bookingDate) {
      alert(t('serviceDetail.alertSelectDate'));
      return;
    }

    if (!serviceCoords || serviceCoords.lat == null) {
      alert(t('serviceDetail.alertSetLocation'));
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      
      // Calculate estimated duration based on price unit
      let estimatedDuration = '1 hour';
      const priceUnit = service?.price_unit || 'hour';
      const isHourly = priceUnit === 'hour' || priceUnit === 'per hour';
      const isDaily = priceUnit === 'day' || priceUnit === 'per day';
      const isProject = priceUnit === 'project' || priceUnit === 'per project';
      if (isHourly && bookingDetails.selectedHours) {
        estimatedDuration = `${bookingDetails.selectedHours} hour${bookingDetails.selectedHours > 1 ? 's' : ''}`;
      } else if (isDaily) {
        const days = bookingDetails.selectedDays || 1;
        estimatedDuration = `${days} day${days > 1 ? 's' : ''}`; // Multi-day support
      } else if (priceUnit === 'session') {
        const sessionDuration = service?.session_duration || 1;
        estimatedDuration = `${sessionDuration} hour${sessionDuration > 1 ? 's' : ''}`;
      } else if (isProject) {
        estimatedDuration = 'Project-based';
      }
      
      // Build end_date as a proper date string for multi-day bookings
      let endDateStr = null;
      if (isDaily && bookingDetails.endDay && bookingDate) {
        // endDay is a day-of-month number; construct full date string from bookingDate
        const startDate = new Date(bookingDate + 'T00:00:00');
        const endDate = new Date(startDate);
        endDate.setDate(bookingDetails.endDay);
        endDateStr = `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`;
      }

      // Ensure we have a readable address for the pin even if the background
      // reverse-geocode hasn't resolved yet (e.g. Nominatim was slow/throttled).
      let resolvedAddress = serviceAddress;
      if (!resolvedAddress) {
        resolvedAddress = await reverseGeocode(serviceCoords.lat, serviceCoords.lng);
      }

      await axios.post(`${API}/bookings`, {
        service_id: id,
        date: bookingDate,
        time: bookingTime || '09:00',
        notes: bookingMessage || null,
        estimated_duration: estimatedDuration,
        price_unit: priceUnit,
        requested_hours: isHourly ? bookingDetails.selectedHours : null,
        requested_days: isDaily ? (bookingDetails.selectedDays || 1) : null,
        end_date: endDateStr,
        total_price: bookingDetails.totalPrice || service?.price,
        service_lat: serviceCoords.lat,
        service_lng: serviceCoords.lng,
        service_address: resolvedAddress || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert(t('serviceDetail.alertBookingSent'));
      setShowBookingModal(false);
      setBookingDate('');
      setBookingTime('');
      setBookingMessage('');
      setServiceCoords(null);
      setServiceAddress('');
      setBookingDetails({ duration: 1, totalPrice: 0, selectedHours: 1, selectedDays: 1 });
      navigate('/dashboard');
    } catch (error) {
      console.error('Error creating booking:', error);
      const detail = error.response?.data?.detail;
      let msg;
      if (typeof detail === 'string') {
        msg = detail;
      } else if (Array.isArray(detail)) {
        msg = detail.map(d => d.msg || d.message || JSON.stringify(d)).join(', ');
      } else {
        msg = 'Failed to create booking. Please try again.';
      }
      alert(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDateTimeSelect = useCallback((date, time) => {
    setBookingDate(date);
    setBookingTime(time || '');
  }, []);

  const handleBookingDetailsChange = useCallback((details) => {
    setBookingDetails(details);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar user={user} onLogout={onLogout} />
        <div className="flex items-center justify-center h-96">
          <div className="text-xl font-semibold text-gray-600">Loading...</div>
        </div>
      </div>
    );
  }

  if (!service) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar user={user} onLogout={onLogout} />
        <div className="max-w-7xl mx-auto px-4 py-8">
          <p>{t('serviceDetail.notFound')}</p>
        </div>
      </div>
    );
  }

  const getCurrencySymbol = (currency) => {
    const symbols = { PHP: '₱', USD: '$', EUR: '€', GBP: '£' };
    return symbols[currency] || currency;
  };

  // Calculate payout amounts using dynamic fee rates from config.
  // Reflect the quantity the client selects in the booking picker (hours/days)
  // so the sidebar "Client Will Pay" updates live instead of staying at 1 unit.
  const clientFeeRate = (feeRates.client_fee_rate || 10) / 100;
  const providerFeeRate = (feeRates.provider_fee_rate || 5) / 100;
  const isHourlyUnit = service && (service.price_unit === 'hour' || service.price_unit === 'per hour');
  const isDailyUnit = service && (service.price_unit === 'day' || service.price_unit === 'per day');
  const quantity = isHourlyUnit
    ? (bookingDetails.selectedHours || 1)
    : isDailyUnit
      ? (bookingDetails.selectedDays || 1)
      : 1;
  const quantityLabel = isHourlyUnit ? 'hr' : isDailyUnit ? 'day' : null;
  const baseAmount = service
    ? ((bookingDetails.totalPrice && bookingDetails.totalPrice > 0)
        ? bookingDetails.totalPrice
        : service.price * quantity)
    : 0;
  const clientFee = baseAmount * clientFeeRate;
  const providerFee = baseAmount * providerFeeRate;
  const totalClientPays = baseAmount + clientFee;
  const providerPayout = baseAmount - providerFee;
  const currency = service?.currency || 'PHP';
  const currencySymbol = getCurrencySymbol(currency);

  return (
    <div className="min-h-screen bg-gray-50" data-testid="service-detail-page">
      <Navbar user={user} onLogout={onLogout} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-600 hover:text-blue-600 mb-6 transition"
          data-testid="back-button"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          <span>{t('serviceDetail.back')}</span>
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Service Images Gallery */}
            {service.images && service.images.length > 0 && (
              <div className="bg-white rounded-lg shadow-md p-4 mb-6" data-testid="service-gallery">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {service.images.map((image, index) => (
                    <img
                      key={`${image}-${index}`}
                      src={image.startsWith('http') ? image : `${BACKEND_URL}${image}`}
                      alt={`${service.title} - Image ${index + 1}`}
                      className="w-full h-48 object-cover rounded-lg"
                    />
                  ))}
                </div>
              </div>
            )}

            <div className="bg-white rounded-lg shadow-md p-8" data-testid="service-details">
              <div className="flex items-center justify-between mb-4">
                <span className="bg-blue-100 text-blue-600 px-4 py-2 rounded-full font-semibold">
                  {service.category}
                </span>
                {service.rating > 0 && (
                  <div className="flex items-center gap-2" data-testid="service-rating">
                    <span className="text-yellow-500 text-2xl">★</span>
                    <span className="text-2xl font-bold">{service.rating}</span>
                    <span className="text-gray-500">({service.total_reviews} {t('serviceDetail.reviews')})</span>
                  </div>
                )}
              </div>

              <h1 className="text-4xl font-bold mb-4" data-testid="service-title">{service.title}</h1>

              {service.location && (
                <p className="text-gray-600 mb-6" data-testid="service-location">
                  📍 {service.location}
                </p>
              )}

              <div className="mb-8">
                <h2 className="text-2xl font-bold mb-4">{t('serviceDetail.description')}</h2>
                <p className="text-gray-700 leading-relaxed whitespace-pre-line" data-testid="service-description">
                  {service.description}
                </p>
              </div>

              {/* Reviews */}
              <div className="border-t pt-8" data-testid="reviews-section">
                <h2 className="text-2xl font-bold mb-6">{t('serviceDetail.reviewsTitle')}</h2>
                {reviews.length === 0 ? (
                  <p className="text-gray-500" data-testid="no-reviews-message">{t('serviceDetail.noReviews')}</p>
                ) : (
                  <div className="space-y-4">
                    {reviews.map((review) => (
                      <div key={review.id} className="border-b pb-4" data-testid={`review-${review.id}`}>
                        <div className="flex items-center gap-2 mb-2">
                          <div className="flex">
                            {[...Array(5)].map((_, i) => (
                              <span key={i} className={i < review.rating ? 'text-yellow-500' : 'text-gray-300'}>
                                ★
                              </span>
                            ))}
                          </div>
                          <span className="font-semibold">{review.client_name}</span>
                          <span className="text-gray-500 text-sm">
                            {new Date(review.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        <p className="text-gray-700">{review.comment}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            {/* Pricing Card */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-6 sticky top-4" data-testid="pricing-card">
              <div className="text-center mb-4">
                <p className="text-4xl font-bold text-blue-600" data-testid="service-price">
                  {currencySymbol}{service.price.toLocaleString()}
                </p>
                <p className="text-gray-600">{service.price_unit}</p>
              </div>

              {/* Pricing Breakdown */}
              <div className="bg-gray-50 rounded-lg p-4 mb-4 text-sm">
                <div className="flex justify-between mb-2">
                  <span className="text-gray-600">
                    {t('serviceDetail.servicePrice')}
                    {quantityLabel && quantity > 1 && (
                      <span className="block text-xs text-gray-400">
                        {currencySymbol}{service.price.toLocaleString()} × {quantity} {quantityLabel}{quantity > 1 ? 's' : ''}
                      </span>
                    )}
                  </span>
                  <span className="font-medium" data-testid="sidebar-base-amount">{currencySymbol}{baseAmount.toLocaleString()}</span>
                </div>
                <div className="flex justify-between mb-2">
                  <span className="text-gray-600">{t('serviceDetail.platformFee')} ({feeRates.client_fee_rate}%)</span>
                  <span className="font-medium text-red-500">+{currencySymbol}{clientFee.toLocaleString()}</span>
                </div>
                <div className="border-t pt-2 mt-2">
                  <div className="flex justify-between font-bold">
                    <span>{t('serviceDetail.clientPays')}</span>
                    <span className="text-blue-600" data-testid="sidebar-client-pays">{currencySymbol}{totalClientPays.toLocaleString()}</span>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-dashed">
                  <div className="flex justify-between text-green-600">
                    <span>{t('serviceDetail.providerReceives')}</span>
                    <span className="font-medium">{currencySymbol}{providerPayout.toLocaleString()}</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">({t('serviceDetail.afterProviderFee', { rate: feeRates.provider_fee_rate })})</p>
                </div>
              </div>

              {user && user.id !== service.provider_id ? (
                <div className="space-y-3">
                  <button
                    onClick={() => setShowBookingModal(true)}
                    className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white py-3 rounded-lg font-semibold hover:shadow-lg transition"
                    data-testid="book-service-button"
                  >
                    {t('serviceDetail.bookThisService')}
                  </button>
                  <button
                    onClick={() => setShowInquiryChat(true)}
                    className="w-full border-2 border-blue-600 text-blue-600 py-3 rounded-lg font-semibold hover:bg-blue-50 transition"
                    data-testid="inquiry-button"
                  >
                    💬 {t('serviceDetail.sendInquiry')}
                  </button>
                </div>
              ) : user && user.id === service.provider_id ? (
                <div className="text-center text-gray-600" data-testid="own-service-message">
                  {t('serviceDetail.thisIsYourService')}
                </div>
              ) : (
                <div className="space-y-3">
                  <Link
                    to="/login"
                    className="block w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white py-3 rounded-lg font-semibold hover:shadow-lg transition text-center"
                    data-testid="login-to-book-button"
                  >
                    {t('serviceDetail.loginToBook')}
                  </Link>
                  <Link
                    to="/login"
                    className="block w-full border-2 border-blue-600 text-blue-600 py-3 rounded-lg font-semibold hover:bg-blue-50 transition text-center"
                  >
                    💬 {t('serviceDetail.loginToInquire')}
                  </Link>
                </div>
              )}
            </div>

            {/* Provider Info */}
            {provider && (
              <div className="bg-white rounded-lg shadow-md p-6" data-testid="provider-info">
                <h3 className="text-lg font-bold mb-4">{t('serviceDetail.aboutProvider')}</h3>
                <Link to={`/provider/${provider.id}`} className="block">
                  <div className="flex items-center gap-4 mb-4">
                    {provider.avatar ? (
                      <img 
                        src={provider.avatar.startsWith('http') ? provider.avatar : `${process.env.REACT_APP_BACKEND_URL}${provider.avatar}`} 
                        alt={provider.name}
                        className="w-16 h-16 rounded-full object-cover"
                        data-testid="provider-avatar"
                      />
                    ) : (
                      <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center text-white text-2xl font-bold">
                        {provider.name.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div>
                      <p className="font-bold text-lg">{provider.name}</p>
                      {provider.rating > 0 && (
                        <div className="flex items-center gap-1">
                          <span className="text-yellow-500">★</span>
                          <span className="font-semibold">{provider.rating}</span>
                          <span className="text-sm text-gray-500">({provider.total_reviews})</span>
                        </div>
                      )}
                    </div>
                  </div>
                </Link>
                {provider.bio && (
                  <p className="text-gray-700 mb-4">{provider.bio}</p>
                )}
                {provider.location && (
                  <p className="text-gray-600 text-sm">📍 {provider.location}</p>
                )}
                {provider.skills && provider.skills.length > 0 && (
                  <div className="mt-4">
                    <p className="text-sm font-semibold text-gray-700 mb-2">{t('serviceDetail.skills')}:</p>
                    <div className="flex flex-wrap gap-2">
                      {provider.skills.map((skill, index) => (
                        <span key={`${skill}-${index}`} className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-sm">
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Booking Modal */}
      {showBookingModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-testid="booking-modal">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold">{t('serviceDetail.bookService')}</h2>
              <button 
                onClick={() => {
                  setShowBookingModal(false);
                  setBookingDate('');
                  setBookingTime('');
                  setServiceCoords(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {/* Service Summary */}
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <h3 className="font-semibold text-gray-800">{service.title}</h3>
              <p className="text-blue-600 font-bold text-lg">{currencySymbol}{service.price.toLocaleString()} <span className="text-gray-500 text-sm font-normal">{service.price_unit}</span></p>
            </div>

            <form onSubmit={handleBooking}>
              {/* Date & Time Picker */}
              <div className="mb-4">
                <label className="block text-gray-700 font-semibold mb-2">
                  {t('serviceDetail.selectDateTime')} <span className="text-red-500">*</span>
                </label>
                <BookingDateTimePicker
                  providerId={service.provider_id}
                  serviceId={service.id}
                  service={service}
                  onDateTimeSelect={handleDateTimeSelect}
                  onBookingDetailsChange={handleBookingDetailsChange}
                  selectedDate={bookingDate}
                  selectedTime={bookingTime}
                />
              </div>

              {/* Message */}
              <div className="mb-6">
                <label className="block text-gray-700 font-semibold mb-2">{t('serviceDetail.messageToProvider')}</label>
                <textarea
                  value={bookingMessage}
                  onChange={(e) => setBookingMessage(e.target.value)}
                  rows="3"
                  placeholder={t('serviceDetail.messagePlaceholder')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid="booking-message-input"
                ></textarea>
              </div>

              {/* Service Location (drop a pin) */}
              <div className="mb-6">
                <label className="block text-gray-700 font-semibold mb-2">
                  {t('serviceDetail.serviceLocation')} <span className="text-red-500">*</span>
                </label>
                {savedLocations.length > 0 && (
                  <div className="mb-2">
                    <select
                      value={selectedSavedId}
                      onChange={(e) => handleSelectSavedLocation(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm"
                      data-testid="saved-location-select"
                    >
                      <option value="">{t('serviceDetail.useSavedLocation', 'Use a saved location…')}</option>
                      {savedLocations.map((loc) => (
                        <option key={loc.id} value={loc.id}>
                          {(loc.label || loc.name || loc.barangay || 'Saved location')}
                          {loc.city ? ` — ${loc.city}` : ''}
                          {((loc.lat == null || loc.lng == null) && !(Array.isArray(loc.coordinates) && loc.coordinates.length === 2)) ? ' (no pin saved)' : ''}
                        </option>
                      ))}
                    </select>
                    {savedLocHint && (
                      <p className="mt-1 text-xs text-amber-600" data-testid="saved-location-hint">{savedLocHint}</p>
                    )}
                  </div>
                )}
                <LocationPicker value={serviceCoords} onChange={(c) => { setServiceCoords(c); setSavedLocHint(''); }} focus={mapFocus} />
              </div>

              {/* Actions */}
              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowBookingModal(false);
                    setBookingDate('');
                    setBookingTime('');
                    setServiceCoords(null);
                    setServiceAddress('');
                    setSelectedSavedId('');
                    setSavedLocHint('');
                    setMapFocus(null);
                  }}
                  className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-lg hover:bg-gray-300 font-semibold"
                  data-testid="booking-cancel-button"
                >
                  {t('serviceDetail.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={submitting || !bookingDate || !serviceCoords}
                  className="flex-1 bg-gradient-to-r from-orange-500 to-orange-600 text-white py-3 rounded-lg hover:shadow-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="booking-submit-button"
                >
                  {submitting ? t('serviceDetail.sending') : t('serviceDetail.confirmBooking')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Inquiry Chat Modal */}
      {showInquiryChat && service && (
        <InquiryChat
          serviceId={service.id}
          providerId={service.provider_id}
          providerName={service.provider_name}
          serviceName={service.title}
          user={user}
          onClose={() => setShowInquiryChat(false)}
        />
      )}

      <Footer />
    </div>
  );
};

export default ServiceDetail;